import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '../api/client'
import { useSession } from '../store/user.store'

const PDF_PATH = '/api/astro/natal/full.pdf'

/**
 * Ask the API to ship the natal PDF into the user's bot chat as a native
 * Document. This is the PRIMARY download path on TG/MAX because the
 * in-app WebView swallows `<a download>` clicks on iOS and most Android.
 *
 * Returns the mutation handle so the UI can show pending/success/error.
 */
export function useSendNatalPdfToChat() {
  return useMutation({
    mutationFn: () => api.sendNatalPdfToChat(),
  })
}

async function fetchPdfFile(jwt: string, userName: string | undefined | null): Promise<File> {
  const res = await fetch(PDF_PATH, { headers: { Authorization: `Bearer ${jwt}` } })
  if (!res.ok) throw new Error(`PDF ${res.status}`)
  const blob = await res.blob()
  return new File([blob], `natal-${userName ?? 'chart'}.pdf`, { type: 'application/pdf' })
}

/**
 * Authenticated download of the natal-chart PDF. Triggers a browser file
 * download (saves to disk). Used for the explicit "Download PDF" button.
 */
export function useDownloadNatalPdf() {
  const jwt = useSession((s) => s.jwt)
  const userName = useSession((s) => s.user?.name)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function download() {
    if (!jwt) return
    setLoading(true)
    setError(null)
    try {
      const file = await fetchPdfFile(jwt, userName)
      const url = URL.createObjectURL(file)
      const a = document.createElement('a')
      a.href = url
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return { download, loading, error }
}

/**
 * Fetcher for the natal PDF as a `File`. Used by ShareButton to attach the
 * PDF to a Web Share invocation. Throws if there's no active session.
 */
export function useFetchNatalPdf(): () => Promise<File> {
  const jwt = useSession((s) => s.jwt)
  const userName = useSession((s) => s.user?.name)
  return async () => {
    if (!jwt) throw new Error('No active session')
    return fetchPdfFile(jwt, userName)
  }
}
